import axios from "axios";
import store from "@/store";
import { isStudioEmbed, getStudioAccessToken, requestStudioAccessToken } from "@/utils/studioEmbed";
import { message } from "ant-design-vue";
import { getMfaToken, stepUpPurpose, requestStepUp, geoBlockedNotice, mfaCheckUnavailableNotice } from "@/utils/mfa";
import { reportGeoBlock } from "@/utils/geoBlockReport";
import { getAccessToken, isSupportTab, isSupportEnded, clearTokens, endSupportTab } from "@/utils/tokenStore";
import { withClientHeaders } from "@/utils/clientHeaders";
import { standardNotice, permissionDeniedText, reloadPermsOnce } from "@/utils/standardErrors";
import { supportEndedUrl, isSupportPagePath } from "@/utils/supportSession";

// 지원 탭이 끝남(401 SUPPORT_SESSION_ENDED 등): 갱신하지 않고 그 탭을 「끝난 지원 탭」으로 묶은 뒤 끝 화면(계약 SUPPORT 9)
// (지원 값만 지우면 그 탭이 일반 탭이 되어 localStorage 의 일반 로그인 토큰으로 다시 보냄 — 래치로 막음)
function goSupportEnded(reason) {
    endSupportTab(reason);
    window.location.replace(supportEndedUrl(reason));
}

const apiInstance = axios.create({
    baseURL: process.env.VUE_APP_SERVERURL,
});

apiInstance.interceptors.request.use(
    (config) => {
        withClientHeaders(config);
        const mfaToken = getMfaToken();
        if (mfaToken) config.headers['X-Mfa-Token'] = mfaToken;
        if (isStudioEmbed()) {
            config.headers.Authorization = `Bearer ${getStudioAccessToken()}`;
            return config;
        }
        // 지원 진입·끝 화면에서는 이 브라우저의 일반 로그인 토큰을 싣지 않음
        if (getAccessToken() && !isSupportPagePath(globalThis.location?.pathname)) {
            config.headers.Authorization = `Bearer ${getAccessToken()}`;
            config.headers['Access-Control-Allow-Origin'] = '*';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isTokenRefreshing = false;
let failedRequestsQueue = [];

apiInstance.interceptors.response.use(
    (response) => {
        if (isStudioEmbed()) getStudioAccessToken();
        store.commit("resetMessageCount");
        return response;
    },
        
    async (error) => {
        const originalRequest = error.config;
        // silent: 사용자가 누르지 않은 뒷단 요청(직원 권한 정기 확인 등) — 어떤 오류도 알리지 않고 그대로 실패(갱신·step-up·로그아웃 없음)
        if (originalRequest?.silent) return Promise.reject(error);
        // 국외 IP 의 모든 요청(403 GEO_BLOCKED): 안내 한 번(같은 key 라 겹치지 않음) 뒤 실패로 끝냄. step-up 은 띄우지 않음.
        // 로그인 요청은 로그인 화면·비밀번호 재확인 화면(HospitalPw)의 대화상자가 안내하므로 토스트를 띄우지 않음(겹침 방지).
        const geoNotice = geoBlockedNotice(error);
        if (geoNotice) {
            reportGeoBlock(originalRequest?.url === '/auth/login' ? 'LOGIN' : 'REQUEST', originalRequest);
            if (originalRequest?.url !== '/auth/login') message.warning({ content: geoNotice, key: 'GEO_BLOCKED' });
            return Promise.reject(error);
        }
        // 2FA 필요 여부 확인 실패(503 MFA_CHECK_UNAVAILABLE): 고정 문구 한 번 뒤 실패로 끝냄. 재전송·step-up 없음(F-9).
        const unavailable = mfaCheckUnavailableNotice(error);
        if (unavailable) {
            message.error({ content: unavailable, key: 'MFA_CHECK_UNAVAILABLE' });
            return Promise.reject(error);
        }
        // 증표 없음·발송 추가 인증 필요(403): step-up 창 → 통과하면 목적마다 한 번만 다시 보냄.
        // 서버가 실행 전에 막은 요청이라 쓰기 요청도 다시 보내도 중복되지 않음.
        const purpose = stepUpPurpose(error);
        if (purpose && originalRequest && !originalRequest.url?.startsWith('/auth/2fa/step-up') &&
            !originalRequest._stepUp?.includes(purpose)) {
            originalRequest._stepUp = [...(originalRequest._stepUp || []), purpose];
            if (await requestStepUp(purpose)) return apiInstance(originalRequest);
            // 사용자가 창을 닫음 → 화면은 「실패」를 띄우지 않음(mfa.alreadyNotified)
            error.stepUpCancelled = true;
            return Promise.reject(error);
        }
        // 새 오류 코드(지원·권한·기록 장치 — W6 E2 표): 같은 key 로 한 번만 알리고, 끝난 지원 탭·정지 계정은 갱신하지 않음
        const supportTab = !isStudioEmbed() && isSupportTab();
        const notice = standardNotice(error, { supportTab });
        // 숨긴 메뉴의 상태만 묻는 뒷단 요청(왼쪽 메뉴·대시보드)은 권한이 없어도 알리지 않음 — 그 화면을 쓰는 게 아님(10-30 I9)
        if (originalRequest?.quietDenied && error.response?.status === 403) return Promise.reject(error);
        if (notice) {
            error.standardNotified = true; // 화면이 같은 실패를 또 띄우지 않게(mfa.alreadyNotified)
            if (notice.action === 'SUPPORT_ENDED') {
                goSupportEnded(error.response.data.reason);
                return Promise.reject(error);
            }
            if (notice.action === 'LOGOUT' && !isStudioEmbed()) {
                message.warning({ content: notice.text, key: notice.key });
                if (supportTab) goSupportEnded('ACCOUNT_INACTIVE');
                else {
                    clearTokens();
                    window.location.href = '/signin';
                }
                return Promise.reject(error);
            }
            if (notice.action === 'RELOAD_PERMS') {
                // 권한을 한 번 다시 읽고(메뉴 다시 그림) 이름표로 문구를 만듦 — 같은 요청을 다시 보내지 않음
                reloadPermsOnce().then((labelOf) =>
                    message.warning({ content: permissionDeniedText(error, labelOf), key: notice.key }));
                return Promise.reject(error);
            }
            if (notice.text) message[notice.level]({ content: notice.text, key: notice.key });
            if (notice.action !== 'LOGOUT') return Promise.reject(error);
        }
        if (isStudioEmbed()) {
            if (error.response?.status === 401 && originalRequest && !originalRequest._studioRetry) {
                originalRequest._studioRetry = true;
                await requestStudioAccessToken();
                // Retrying a mutation could issue a certificate or send a message twice.
                if (['get', 'head', 'options'].includes((originalRequest.method || 'get').toLowerCase())) {
                    originalRequest.headers.Authorization = `Bearer ${getStudioAccessToken()}`;
                    return apiInstance(originalRequest);
                }
            }
            return Promise.reject(error);
        }
        // 끝난 지원 탭: 갱신도 재전송도 하지 않음
        if (isSupportEnded()) return Promise.reject(error);
        if(originalRequest.url && originalRequest.url !== '/auth/login') {
            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                if (!isTokenRefreshing) {
                    
                    isTokenRefreshing = true;
                    await store.dispatch('requestAccessToken');
                    isTokenRefreshing = false;

                    // 갱신하다 지원이 끝났으면(SUPPORT_SESSION_ENDED) 기다리던 요청까지 모두 실패 — 다른 토큰으로 다시 보내지 않음
                    // (반환값으로 판정하지 않음: 정상 갱신도 undefined 를 돌려줌)
                    if (isSupportEnded()) {
                        failedRequestsQueue.forEach((queueItem) => queueItem.reject(error));
                        failedRequestsQueue = [];
                        return Promise.reject(error);
                    }

                    failedRequestsQueue.forEach((queueItem) => {
                        queueItem.resolve(apiInstance(queueItem.originalRequest));
                    });
                    failedRequestsQueue = [];

                    originalRequest._retry = true;
                    originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
                    return apiInstance(originalRequest);
                } else {
                        return new Promise((resolve, reject) => {
                            failedRequestsQueue.push({
                            resolve,
                            reject,
                            originalRequest,
                        });
                    });
                }
            }
        }
        
        return Promise.reject(error);
    }
);

export default apiInstance;
